import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import api from "../hooks/api";
import { API_URLS } from "../App";
import ListOrderItems from "../components/Orders/ListOrderItems";
import ManageOrder from "../components/Orders/ManageOrder";
import ManageOrderActions from "../components/Orders/ManageOrderActions";
import { useLayoutContext } from "../context/LayoutContext";
import OrderFilters from "../components/Orders/OrderFilters";
import OrderRejectCancelModal from "../components/Orders/OrderRejectCancelModal";

interface OrderSearchParams {
  status?: string;
  customer?: string;
  type?: string;
}

const OrderPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    userSettings,
    setUserSettings,
    systemError,
    setSystemError,
    //loading,
    //setLoading,
    error,
    setError,
    success,
    setSuccess,
    modalScreen,
    setModalScreen,
    page,
    setPage,
  } = useLayoutContext();

  const { id } = useParams<{ id: string }>();
  const [orderId, setOrderId] = useState<string | undefined>(id);
  const [orders, setOrders] = useState<Order[]>([]);
  const [filters, setFilters] = useState<OrderSearchParams>({
    status: window.location.search.includes("status=")
      ? new URLSearchParams(window.location.search).get("status") || ""
      : "",
    customer: window.location.search.includes("customer=")
      ? new URLSearchParams(window.location.search).get("customer") || ""
      : "",
    type: window.location.search.includes("type=")
      ? new URLSearchParams(window.location.search).get("type") || ""
      : "",
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [perPage, setPerPage] = useState<number>(
    window.location.search.includes("per_page=")
      ? parseInt(
          new URLSearchParams(window.location.search).get("per_page") || "10",
        )
      : 10,
  );
  const [sortBy, setSortBy] = useState<string>("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null);

  const fetchOrder = async (orderId: string, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await api.get(API_URLS.ORDERS + `/${orderId}`);
      setCurrentOrder(response.data);
      setModalScreen({
        state: "edit",
        title: `Gerir Encomenda: #${response.data.id}`,
        childrenControlOverflow: true,
        children: (
          <ManageOrder order={response.data} onAddNote={handleAddNote} />
        ),
        footer: (
          <ManageOrderActions
            order={response.data}
            handleConfirmOrder={handleConfirmOrder}
            handleRejectOrder={handleRejectOrder}
            handleCancelOrder={handleCancelOrder}
            onOpenRejectModal={() => setIsRejectModalOpen(true)}
            onOpenCancelModal={() => setIsCancelModalOpen(true)}
            onResubmitToErp={handleResubmitToErp}
          />
        ),
        onShow: () => {
          handleManageOrder(response.data);
        },
        onClose: handleCloseModal,
      });
    } catch (error: any) {
      if (!silent) setSystemError(error.message || "Erro ao buscar encomenda.");
      console.warn("Error fetching order:", error.message);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Poll while the ERP submission is in flight so the status card updates without a reload.
  useEffect(() => {
    const status = currentOrder?.erp_submission_status;
    if (!orderId || (status !== "PENDING" && status !== "SENDING")) return;
    const timer = setInterval(() => {
      fetchOrder(orderId, true);
    }, 3000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrder?.erp_submission_status, orderId]);

  const fetchOrders = async () => {
    const loading = () => {
      if (!id) {
        setLoading(true);
        return () => {
          setTimeout(() => setLoading(false), 500);
        };
      }
      setOrders([]);
      return () => {};
    };
    const closeLoading = loading();
    try {
      const response = await api.get(
        API_URLS.ORDERS +
          `/?page=${page}&per_page=${perPage}` +
          (filters.status
            ? `&status=${encodeURIComponent(filters.status)}`
            : "") +
          (filters.customer
            ? `&customer=${encodeURIComponent(filters.customer)}`
            : "") +
          (filters.type ? `&type=${encodeURIComponent(filters.type)}` : "") +
          `&sort_by=${sortBy}&sort_direction=${sortOrder}`,
      );
      setOrders(response.data.orders || []);
      setTotalPages(response.data.total_pages || 1);
      setTotalResults(response.data.total_results || 0);
    } catch (error: any) {
      console.warn("Error fetching offers:", error.message);
    } finally {
      closeLoading();
    }
  };

  const handleConfirmOrder = async (orderId: string) => {
    setLoading(true);
    try {
      await api.post(API_URLS.ORDERS + `/${orderId}/_actions/confirm`);
      fetchOrder(orderId); // Refresh orders after accepting
    } catch (error: any) {
      console.warn("Error accepting order:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRejectOrder = async (
    orderId: string,
    reason: string,
    notes: string,
    visibleToReseller: boolean,
  ) => {
    setLoading(true);
    try {
      await api.post(
        API_URLS.ORDERS +
          `/${orderId}/_actions/reject?reason=${encodeURIComponent(reason)}` +
          (notes ? `&notes=${encodeURIComponent(notes)}` : "") +
          `&visible_to_reseller=${visibleToReseller}`,
      );
      setIsRejectModalOpen(false);
      fetchOrder(orderId); // Refresh order after rejecting
    } catch (error: any) {
      console.warn("Error rejecting order:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async (
    orderId: string,
    reason: string,
    notes: string,
    visibleToReseller: boolean,
  ) => {
    setLoading(true);
    try {
      await api.post(
        API_URLS.ORDERS +
          `/${orderId}/_actions/cancel?reason=${encodeURIComponent(reason)}` +
          (notes ? `&notes=${encodeURIComponent(notes)}` : "") +
          `&visible_to_reseller=${visibleToReseller}`,
      );
      setIsCancelModalOpen(false);
      fetchOrder(orderId); // Refresh order after canceling
    } catch (error: any) {
      console.warn("Error canceling order:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async (content: string, visibleToReseller: boolean) => {
    if (!currentOrder) return;
    setLoading(true);
    try {
      await api.post(
        API_URLS.ORDERS +
          `/${currentOrder.id}/_actions/add-note?content=${encodeURIComponent(content)}&visible_to_reseller=${visibleToReseller}`,
      );
      fetchOrder(currentOrder.id); // Refresh order after adding note
    } catch (error: any) {
      console.warn("Error adding note:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResubmitToErp = async (
    orderId: string,
    acknowledgeDuplicateRisk: boolean,
  ) => {
    // UNKNOWN means the ERP may already have created the order — require an explicit ack.
    if (acknowledgeDuplicateRisk) {
      const ok = window.confirm(
        "Esta encomenda está com estado UNKNOWN no ERP: o ERP pode já a ter criado. " +
          "Reenviar pode duplicar a encomenda. Confirmar o reenvio?",
      );
      if (!ok) return;
    }
    setLoading(true);
    try {
      await api.post(
        API_URLS.ORDERS +
          `/${orderId}/_actions/erp-resubmit?acknowledge_duplicate_risk=${acknowledgeDuplicateRisk}`,
      );
      fetchOrder(orderId); // Refresh order after resubmitting
    } catch (error: any) {
      console.warn("Error resubmitting order to ERP:", error.message);
      setError(
        error?.response?.data?.message ||
          error?.response?.data?.reason ||
          error.message ||
          "Erro ao reenviar a encomenda para o ERP.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleManageOrder = (order: Order) => {
    setOrderId(order.id);
    setCurrentOrder(order);
    setModalScreen({
      state: "edit",
      title: `Gerir Encomenda: #${order.id}`,
      childrenControlOverflow: true,
      children: <ManageOrder order={order} onAddNote={handleAddNote} />,
      footer: (
        <ManageOrderActions
          order={order}
          handleConfirmOrder={handleConfirmOrder}
          handleRejectOrder={handleRejectOrder}
          handleCancelOrder={handleCancelOrder}
          onOpenRejectModal={() => setIsRejectModalOpen(true)}
          onOpenCancelModal={() => setIsCancelModalOpen(true)}
          onResubmitToErp={handleResubmitToErp}
        />
      ),
      onShow: () => {
        handleManageOrder(order);
      },
      onClose: handleCloseModal,
    });
    history.pushState(history.state, "", `/orders/${order.id}`);
  };

  const handleCloseModal = useCallback(() => {
    setOrderId(undefined);
    setModalScreen(null);
    history.pushState(
      history.state,
      "",
      `/orders?page=${page}&per_page=${perPage}` +
        (filters.status
          ? `&status=${encodeURIComponent(filters.status)}`
          : "") +
        (filters.customer
          ? `&customer=${encodeURIComponent(filters.customer)}`
          : "") +
        (filters.type ? `&type=${encodeURIComponent(filters.type)}` : ""),
    );
    fetchOrders(); // Refresh offers after managing
  }, [page, perPage, filters, setModalScreen]);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate("/login", { state: { from: pathname } });
    }
    if (orderId) {
      console.log("open order with id:", orderId);
      fetchOrder(orderId);
    }
    fetchOrders();
  }, [orderId, page, perPage, sortOrder, sortBy]);

  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    } else {
      fetchOrders();
    }
  }, [filters]);

  useEffect(() => {
    if (!orderId) {
      history.replaceState(
        history.state,
        "",
        `/orders?page=${page}&per_page=${perPage}` +
          (filters.status
            ? `&status=${encodeURIComponent(filters.status)}`
            : "") +
          (filters.customer
            ? `&customer=${encodeURIComponent(filters.customer)}`
            : "") +
          (filters.type ? `&type=${encodeURIComponent(filters.type)}` : ""),
      );
    }
  }, [perPage, filters]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    history.pushState(
      history.state,
      "",
      `/orders?page=${newPage}&per_page=${perPage}` +
        (filters.status
          ? `&status=${encodeURIComponent(filters.status)}`
          : "") +
        (filters.customer
          ? `&customer=${encodeURIComponent(filters.customer)}`
          : "") +
        (filters.type ? `&type=${encodeURIComponent(filters.type)}` : ""),
    );
  };

  if (!isAuthenticated()) {
    return <div>You must be logged in to edit items.</div>;
  }

  return (
    <>
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="flex flex-col gap-6">
          {/* Header Section */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <span className="text-3xl font-bold text-gray-900 tracking-tight">
                Encomendas
              </span>
              <p className="text-gray-600 mt-1">
                Gerencie encomendas, confirmações e acompanhe o status de
                entrega.
              </p>
            </div>
          </div>

          {/* Filters and Content */}
          <div className="flex flex-col gap-4">
            <OrderFilters
              initialOrderSearchParams={filters}
              onFilterChange={setFilters}
              loading={orders.length == 0 && totalPages > 0}
            />

            <div className="overflow-auto">
              <ListOrderItems
                orders={orders}
                page={page}
                totalPages={totalPages}
                perPage={perPage}
                setPerPage={setPerPage}
                setSortBy={setSortBy}
                sortBy={sortBy}
                setSortOrder={setSortOrder}
                sortOrder={sortOrder}
                handlePageChange={handlePageChange}
                openManageOrderModal={handleManageOrder}
                totalResults={totalResults}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>

      {currentOrder && (
        <>
          <OrderRejectCancelModal
            isOpen={isRejectModalOpen}
            onClose={() => setIsRejectModalOpen(false)}
            onSubmit={(reason, notes, visibleToReseller) =>
              handleRejectOrder(
                currentOrder.id,
                reason,
                notes,
                visibleToReseller,
              )
            }
            actionType="reject"
            orderId={currentOrder.id}
          />

          <OrderRejectCancelModal
            isOpen={isCancelModalOpen}
            onClose={() => setIsCancelModalOpen(false)}
            onSubmit={(reason, notes, visibleToReseller) =>
              handleCancelOrder(
                currentOrder.id,
                reason,
                notes,
                visibleToReseller,
              )
            }
            actionType="cancel"
            orderId={currentOrder.id}
          />
        </>
      )}
    </>
  );
};

export default OrderPage;
